import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, Download } from "lucide-react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import Seo from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function DiscoveryAdmin() {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState({ items: [], statuses: [] });
  const [vendors, setVendors] = useState([]);
  const [feedback, setFeedback] = useState({ items: [], statuses: [] });
  const [claims, setClaims] = useState([]);
  const [cats, setCats] = useState([]);
  const [newCat, setNewCat] = useState("");

  const loadEnq = () => api.get("/discovery/admin/enquiries?limit=50").then((r) => setEnquiries(r.data)).catch(() => toast.error("Admin only"));
  const loadVend = () => api.get("/discovery/admin/vendors?limit=50").then((r) => setVendors(r.data.items)).catch(() => {});
  const loadFb = () => api.get("/discovery/admin/feedback").then((r) => setFeedback(r.data)).catch(() => {});
  const loadClaims = () => api.get("/discovery/admin/vendor-claims").then((r) => setClaims(r.data)).catch(() => {});
  const loadCats = () => api.get("/discovery/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { loadEnq(); loadVend(); loadFb(); loadClaims(); loadCats(); }, []);

  const setEnqStatus = async (id, status) => { try { await api.put(`/discovery/admin/enquiries/${id}/status`, { status }); toast.success("Updated"); loadEnq(); } catch { toast.error("Failed"); } };
  const updVendor = async (id, patch) => { try { await api.put(`/discovery/admin/vendors/${id}`, patch); toast.success("Updated"); loadVend(); } catch { toast.error("Failed"); } };
  const approveVendor = async (id) => { try { await api.post(`/discovery/admin/vendors/${id}/approve`); toast.success("Vendor approved"); loadVend(); } catch { toast.error("Failed"); } };
  const modFb = async (id, status) => { try { await api.put(`/discovery/admin/feedback/${id}`, { status, verification_method: "Admin review" }); toast.success("Updated"); loadFb(); } catch { toast.error("Failed"); } };
  const addCat = async () => { if (!newCat.trim()) return; try { await api.post("/discovery/admin/categories", { name: newCat }); toast.success("Category added"); setNewCat(""); loadCats(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const deactivateCat = async (id) => { try { await api.delete(`/discovery/admin/categories/${id}`); toast.success("Deactivated"); loadCats(); } catch { toast.error("Failed"); } };
  const exportCsv = () => { const t = localStorage.getItem("ca_token"); window.open(`${API}/discovery/admin/enquiries/export?auth=${t}`, "_blank"); };

  const pendingVendors = vendors.filter((v) => v.verification_status === "Pending Review");

  return (
    <div className="ca-container py-10">
      <Seo title="Discovery Admin | ComplaintAdvisor" noindex />
      <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2"><Store className="h-7 w-7 text-teal" /> Discovery Admin</h1>
      <div className="mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="enquiries" data-testid="da-tab-enquiries">Enquiries ({enquiries.items.length})</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="da-tab-vendors">Vendors{pendingVendors.length ? ` · ${pendingVendors.length} pending` : ""}</TabsTrigger>
            <TabsTrigger value="feedback" data-testid="da-tab-feedback">Feedback ({feedback.items.length})</TabsTrigger>
            <TabsTrigger value="claims" data-testid="da-tab-claims">Claims ({claims.length})</TabsTrigger>
            <TabsTrigger value="categories" data-testid="da-tab-categories">Categories</TabsTrigger>
          </TabsList>

          <TabsContent value="enquiries" className="mt-4">
            <div className="flex justify-end mb-2"><Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-enquiries-btn"><Download className="h-4 w-4 mr-1" /> Export CSV</Button></div>
            <div className="space-y-2">
              {enquiries.items.map((e) => (
                <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`enq-row-${e.enquiry_id}`}>
                  <div>
                    <p className="font-mono text-xs text-slate-400">{e.enquiry_id}</p>
                    <p className="text-sm font-medium text-slate-800">{e.name} · {e.category_name}{e.subcategory_name ? ` / ${e.subcategory_name}` : ""}</p>
                    <p className="text-xs text-slate-500">{e.mobile} · {e.city || "—"} · {e.service_required || e.description?.slice(0, 40)}</p>
                  </div>
                  <Select value={e.status} onValueChange={(v) => setEnqStatus(e.id, v)}>
                    <SelectTrigger className="w-52" data-testid={`enq-status-${e.enquiry_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{enquiries.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              ))}
              {!enquiries.items.length && <p className="text-sm text-slate-400">No enquiries yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="vendors" className="mt-4 space-y-2">
            {vendors.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`vendor-row-${v.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{v.name} <span className="text-xs text-slate-400">· {v.verification_status}</span></p>
                  <p className="text-xs text-slate-500">{v.category_slug} · {v.city} · {v.is_demo ? "Demo" : v.listed ? "Listed" : "Not listed"}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {v.verification_status === "Pending Review" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => approveVendor(v.id)} data-testid={`vendor-approve-${v.id}`}>Approve</Button>}
                  <Button size="sm" variant="outline" onClick={() => updVendor(v.id, { featured: !v.featured })} data-testid={`vendor-feature-${v.id}`}>{v.featured ? "Unfeature" : "Feature"}</Button>
                  <Button size="sm" variant="outline" onClick={() => updVendor(v.id, { sponsored: !v.sponsored })} data-testid={`vendor-sponsor-${v.id}`}>{v.sponsored ? "Unsponsor" : "Sponsor"}</Button>
                  <Button size="sm" variant="outline" onClick={() => updVendor(v.id, { contact_consent: !v.contact_consent })} data-testid={`vendor-consent-${v.id}`}>{v.contact_consent ? "Hide contact" : "Show contact"}</Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="feedback" className="mt-4 space-y-2">
            {feedback.items.map((fb) => (
              <div key={fb.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`fb-row-${fb.public_id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{fb.name} → {fb.vendor_name} <span className="text-xs text-slate-400">· {fb.status} · {fb.rating}★</span></p>
                  <p className="text-xs text-slate-500 max-w-xl">{fb.relationship} — “{fb.text}”</p>
                </div>
                <Select value={fb.status} onValueChange={(v) => modFb(fb.public_id, v)}>
                  <SelectTrigger className="w-44" data-testid={`fb-status-${fb.public_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{feedback.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ))}
            {!feedback.items.length && <p className="text-sm text-slate-400">No feedback submitted yet.</p>}
          </TabsContent>

          <TabsContent value="claims" className="mt-4 space-y-2">
            {claims.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-medium text-slate-800">{c.claim_id} · {c.vendor_name}</p>
                <p className="text-xs text-slate-500">{c.name} · {c.mobile} · {c.email} — {c.message || "—"}</p>
              </div>
            ))}
            {!claims.length && <p className="text-sm text-slate-400">No claim requests.</p>}
          </TabsContent>

          <TabsContent value="categories" className="mt-4">
            <div className="flex gap-2 items-end mb-4 rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex-1"><label className="text-xs text-slate-500">New category name</label><Input value={newCat} onChange={(e) => setNewCat(e.target.value)} data-testid="new-cat-name" /></div>
              <Button onClick={addCat} className="bg-teal-brand text-white" data-testid="add-cat-btn">Add</Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {cats.map((c) => (
                <div key={c.slug} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between">
                  <span className="text-sm text-slate-700">{c.name} <span className="text-xs text-slate-400">({c.subcategories?.length || 0})</span></span>
                  <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => deactivateCat(c.id)} data-testid={`deactivate-cat-${c.slug}`}>Deactivate</Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
